import imageCompression from 'browser-image-compression';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { getIsExplicitContent } from '@/shared/utils/getIsExplicitContent';
import ExplicitContentService from '@/shared/api/services/ExplicitContentService';
export const toBytes = (mb) => mb * 1024 * 1024;
const useFileUploader = (onMaxSize, onExplicitContent, { maxSizeMB = 10, defaultFileData = null } = {}) => {
    const [fileData, setFileData] = useState(defaultFileData);
    const [key, setKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const savedOnMaxSizeCallback = useRef(null);
    const savedOnExplicitContentCallback = useRef(null);
    useEffect(() => {
        savedOnMaxSizeCallback.current = onMaxSize;
    }, [onMaxSize]);
    useEffect(() => {
        savedOnExplicitContentCallback.current = onExplicitContent;
    }, [onExplicitContent]);
    const handleFileReset = useCallback(() => {
        setFileData(null);
        setKey((prevKey) => prevKey + 1);
    }, []);
    const handleFileChange = useCallback(async (e) => {
        if (!e.target.files)
            return;
        const maxSize = toBytes(maxSizeMB);
        const file = e.target.files[0];
        if (!file)
            return;
        if (file.size >= maxSize) {
            savedOnMaxSizeCallback.current?.(maxSizeMB);
            return;
        }
        setLoading(true);
        setFileData(null);
        const reader = new FileReader();
        reader.onload = async (event) => {
            const data = event.target?.result?.toString();
            const splitData = data?.split(';base64,');
            if (file.type.startsWith('image/')) {
                const options = {
                    maxSizeMB,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    maxIteration: 30,
                };
                const compressedImage = await imageCompression(file, options);
                const imgReader = new FileReader();
                imgReader.readAsDataURL(compressedImage);
                imgReader.onload = async (imgEvent) => {
                    const imgData = imgEvent.target?.result?.toString();
                    const isExplicitContent = await new Promise((res) => {
                        setTimeout(async () => {
                            const predictions = await ExplicitContentService.checkExplicitContent({
                                image: imgData,
                            });
                            if (!predictions || isAxiosError(predictions)) {
                                return res(true);
                            }
                            res(getIsExplicitContent(predictions.data));
                        }, 200);
                    });
                    if (isExplicitContent) {
                        setLoading(false);
                        savedOnExplicitContentCallback.current?.();
                        return;
                    }
                    setFileData({
                        name: file.name,
                        type: file.type,
                        format: splitData?.[0],
                        data: imgData,
                    });
                    setLoading(false);
                };
            }
            else {
                // For non-image files
                setFileData({
                    name: file.name,
                    type: file.type,
                    format: splitData?.[0],
                    data,
                });
                setLoading(false);
            }
        };
        if (file.type.startsWith('text/') || file.type === 'application/json') {
            reader.readAsText(file);
        }
        else {
            reader.readAsDataURL(file);
        }
    }, [maxSizeMB]);
    return { key, fileData, handleFileChange, handleFileReset, setFileData, loading };
};
export default useFileUploader;
